// src/pages/admin/DataManagementPage.js - גרסה מתוקנת עם עיצוב טוב יותר
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../services/firebase/firebaseConfig';
import adminAPI from '../../services/adminApi';
import { 
  Search, Database, RefreshCw, Edit3, Plus, 
  AlertCircle, CheckCircle, Loader, Filter,
  TrendingUp, TrendingDown, Minus, Eye,
  BarChart3, PieChart, Calendar, DollarSign,
  ExternalLink
} from 'lucide-react';
import AdminNavbar from '../../components/admin/AdminNavbar';

// רכיב כרטיס מקור נתונים
const DataSourceCard = ({ source, onRefresh, onViewAll, isRefreshing }) => {
  const getStatusColor = (healthy) => healthy ? 'text-green-600' : 'text-yellow-600';
  const getStatusIcon = (healthy) => healthy ? CheckCircle : AlertCircle;
  
  const StatusIcon = getStatusIcon(source.healthy);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${source.bgColor}`}>
            <Database className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{source.name}</h3>
            <p className="text-sm text-gray-500">{source.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-5 h-5 ${getStatusColor(source.healthy)}`} />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-gray-500">סה״כ קרנות</p>
          <p className="text-xl font-bold text-gray-900">{source.totalFunds}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">עדכון אחרון</p>
          <p className="text-sm text-gray-700">{source.lastUpdate}</p>
        </div>
      </div>
      
      <div className="flex gap-2">
        <button
          onClick={() => onViewAll(source.key)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm"
        >
          <Eye className="w-4 h-4" />
          צפה בכל
        </button>
        <button
          onClick={() => onRefresh(source.key)}
          disabled={isRefreshing}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-50 hover:bg-gray-100 disabled:bg-gray-200 text-gray-700 disabled:text-gray-500 rounded-lg transition-colors text-sm"
        >
          {isRefreshing ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          עדכן
        </button>
      </div>
    </div>
  );
};

// רכיב תוצאות חיפוש
const SearchResults = ({ results, loading, query, onViewFund }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Loader className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
        <p className="text-gray-600">מחפש...</p>
      </div>
    );
  }

  if (!query) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">חיפוש קרנות</h3>
        <p className="text-gray-600">הזן מזהה קרן או שם קרן לחיפוש</p>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
        <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">לא נמצאו תוצאות</h3>
        <p className="text-gray-600">לא נמצאו קרנות המתאימות לחיפוש "{query}"</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-bold text-gray-900">
          נמצאו {results.length} תוצאות עבור "{query}"
        </h3>
      </div>
      <div className="divide-y divide-gray-200">
        {results.map((fund, index) => (
          <FundResultCard key={fund.id || index} fund={fund} onViewFund={onViewFund} />
        ))}
      </div>
    </div>
  );
};

// רכיב קארד תוצאת חיפוש מקוצרת
const FundResultCard = ({ fund, onViewFund }) => {
  const formatReturn = (value) => {
    if (value === null || value === undefined) return 'לא זמין';
    const num = parseFloat(value);
    if (isNaN(num)) return 'לא זמין';
    
    const color = num > 0 ? 'text-green-600' : num < 0 ? 'text-red-600' : 'text-gray-600';
    const icon = num > 0 ? TrendingUp : num < 0 ? TrendingDown : Minus;
    const Icon = icon;
    
    return (
      <div className={`flex items-center gap-1 ${color}`}>
        <Icon className="w-3 h-3" />
        <span>{num.toFixed(2)}%</span>
      </div>
    );
  };

  return (
    <div className="p-6 hover:bg-gray-50 transition-colors">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-lg font-bold text-gray-900 truncate">{fund.name}</h4>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full shrink-0">
              ID: {fund.id}
            </span>
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full shrink-0">
              {fund.sourceLabel || fund.source}
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-2 truncate">{fund.company}</p>
          
          {/* תשואות בשורה */}
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-gray-500">חודש: </span>
              {formatReturn(fund.returns?.month1)}
            </div>
            <div>
              <span className="text-gray-500">שנה: </span>
              {formatReturn(fund.returns?.year1)}
            </div>
            <div>
              <span className="text-gray-500">דמי ניהול: </span>
              <span className="text-orange-600">
                {fund.fees?.management ? `${fund.fees.management.toFixed(2)}%` : 'לא זמין'}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2 ml-4">
          <button
            onClick={() => onViewFund(fund)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm whitespace-nowrap"
          >
            <ExternalLink className="w-4 h-4" />
            צפה בפרטים
          </button>
        </div>
      </div>
    </div>
  );
};

const DataManagementPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState('all');
  const [dataSources, setDataSources] = useState([]);
  const [refreshingSource, setRefreshingSource] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // בדיקת אימות
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (!user) {
        navigate('/admin/login');
      } else {
        loadSourcesData();
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const loadSourcesData = async () => {
    try {
      setError(null);
      const metadata = await adminAPI.getAllSourcesMetadata();
      setDataSources(metadata.sources);
      
      
    } catch (error) {
      console.error('❌ Error loading sources:', error);
      setError(`שגיאה בטעינת נתוני המקורות: ${error.message}`);
      setDataSources([]);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
     
      
      const result = await adminAPI.searchFundsAdvanced(searchQuery, {
        source: selectedSource,
        limit: 20
      });
      
      setSearchResults(result.results);
      
      
    } catch (error) {
      console.error('❌ Admin: Search error:', error);
      setError(`שגיאה בחיפוש: ${error.message}`);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshSource = async (sourceKey) => {
    setRefreshingSource(sourceKey);
    setError(null);
    
    try {
    
      
      const result = await adminAPI.refreshDataSource(sourceKey);
      

      
      // עדכון הנתונים לאחר רענון מוצלח
      await loadSourcesData();
      
    } catch (error) {
      console.error(`❌ Admin: Error refreshing ${sourceKey}:`, error);
      setError(`שגיאה בעדכון ${sourceKey}: ${error.message}`);
    } finally {
      setRefreshingSource(null);
    }
  };

  const handleViewAllSource = (sourceKey) => {
    
    navigate(`/admin/data/source/${sourceKey}`);
  };

  // פונקציה חדשה לצפייה בפרטי קרן
  const handleViewFund = (fund) => {
   
    // מעבר לדף פרטי הקרן עם המידע
    navigate(`/admin/fund/${fund.id}`, { 
      state: { 
        fund: fund,
        source: fund.source || fund._source
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNavbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* כותרת */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">ניהול נתונים</h1>
          <p className="text-gray-600">צפייה, חיפוש ועריכה של נתוני קרנות מכל המקורות</p>
        </div>

        {/* הודעות שגיאה כלליות */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h4 className="text-red-800 font-medium">שגיאה</h4>
              <p className="text-red-700 text-sm mt-1 break-words">{error}</p>
              <button 
                onClick={loadSourcesData}
                className="text-red-600 hover:text-red-800 text-sm font-medium mt-2 underline"
              >
                נסה שוב
              </button>
            </div>
          </div>
        )}

        {/* מקורות נתונים */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">מקורות נתונים</h2>
          {dataSources.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 text-center">
              <AlertCircle className="w-8 h-8 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-yellow-800 mb-2">אין נתונים זמינים</h3>
              <p className="text-yellow-700">לא ניתן לטעון נתוני מקורות. בדוק את החיבור ל-API.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {dataSources.map(source => (
                <DataSourceCard
                  key={source.key}
                  source={source}
                  onRefresh={handleRefreshSource}
                  onViewAll={handleViewAllSource}
                  isRefreshing={refreshingSource === source.key}
                />
              ))}
            </div>
          )}
        </div>

        {/* חיפוש */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">חיפוש קרנות</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex flex-col sm:flex-row gap-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="חפש לפי מזהה קרן (למשל: 962) או שם קרן..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                />
              </div>
              <div className="flex gap-2">
                <select
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 whitespace-nowrap"
                >
                  <option value="all">כל המקורות</option>
                  <option value="gamal-net">גמל-נט</option>
                  <option value="pension-net">פנסיה-נט</option>
                  <option value="insurance-net">ביטוח-נט</option>
                </select>
                <button
                  onClick={handleSearch}
                  disabled={loading || !searchQuery.trim()}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors whitespace-nowrap"
                >
                  {loading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                  חפש
                </button>
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span>מקור: {selectedSource === 'all' ? 'כל המקורות' : dataSources.find(s => s.key === selectedSource)?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                <span>סה״כ {dataSources.reduce((sum, s) => sum + (parseInt(s.totalFunds) || 0), 0)} קרנות במערכת</span>
              </div>
            </div>
          </div>
        </div>

        {/* תוצאות חיפוש */}
        <SearchResults 
          results={searchResults} 
          loading={loading} 
          query={searchQuery}
          onViewFund={handleViewFund}
        />
      </div>
    </div>
  );
};

export default DataManagementPage;